import React, { useState, useEffect } from 'react'
import { XIcon, SaveIcon, CalendarIcon, DollarIcon, MessageIcon, PlusIcon } from './Icons'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { PDFDownloadLink } from '@react-pdf/renderer'
import DealPDF from './DealPDF'

// Add Person interface
interface Person {
  id: number
  name: string
  email?: string
  phone?: string
  is_primary: boolean
}

interface DealCustomer {
  id: number
  customer_id: number
  name: string
  email?: string
  phone?: string
  role: 'applicant' | 'co_applicant'
  order_position: number
}

interface Deal {
  id: number
  name?: string
  title: string
  amount: string
  customer_name: string
  customer_phone?: string
  customer_email?: string
  customer_id: number
  deal_type_id?: string
  type_name?: string
  commission?: {
    totalPoints: number
    totalPercentage: number
    grossCommission: number
    agencyFee: number
    closingFeeAmount: number
    netCommission: number
  }
  base_points: string
  bonus_points: string
  volume_points: string
  closing_date?: string
  expected_close_date?: string
  status_id: string
  status_name: string
  status_color: string
  status_is_archived?: boolean
  status_is_closed?: boolean
  status_is_pending_compliance?: boolean
  position: number
  description?: string
  created_at: string
  updated_at: string
  financial_flag?: string
  financial_flag_updated_at?: string
  deal_customers?: DealCustomer[] // Applicant + co-applicants
}

interface DealHistory {
  id: number
  action_type: string
  previous_values?: string
  new_values?: string
  comment?: string
  comment_type?: string
  field_changed?: string
  created_at: string
  created_by_username?: string
  first_name?: string
  last_name?: string
}

interface Commission {
  totalPoints: number
  totalPercentage: number
  grossCommission: number
  agencyFee: number
  closingFeeAmount: number
  netCommission: number
}

interface DealType {
  id: number
  name: string
}

interface Customer {
  id: number
  name: string
  email?: string
  phone?: string
}

interface DealEditModalProps {
  deal: Deal
  isOpen: boolean
  onSave: (deal: Deal) => void
  onDelete: (deal: Deal) => void
  onClose: () => void
  readOnly?: boolean
  statuses?: any[] // Optional: Pass in custom statuses (e.g., for admin viewing other users' deals)
}

interface Comment {
  id: number
  content: string
  created_at: string
  created_by_username: string
  first_name?: string
  last_name?: string
}

const DealEditModal: React.FC<DealEditModalProps> = ({ deal, onSave, onDelete, onClose, readOnly = false, statuses }) => {
  const [editedDeal, setEditedDeal] = useState<Deal>(deal)
  const [history, setHistory] = useState<DealHistory[]>([])
  const [commission, setCommission] = useState<Commission | null>(null)
  const [dealTypes, setDealTypes] = useState<DealType[]>([])
  const [dealStatuses, setDealStatuses] = useState<any[]>(statuses || [])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [customerSearch, setCustomerSearch] = useState('')
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const [dealCustomers, setDealCustomers] = useState<DealCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentType, setCommentType] = useState('comment')
  const [addingComment, setAddingComment] = useState(false)
  const [financialSettings, setFinancialSettings] = useState<{
    agencyFeePercentage: number
    closingFeeAmount: number
  }>({ agencyFeePercentage: 0, closingFeeAmount: 0 })
  const [liveCommission, setLiveCommission] = useState<Commission | null>(null)
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)
  const [comments, setComments] = useState<Comment[]>([])
  const [pdfLanguage, setPdfLanguage] = useState<'en' | 'fr'>('en')
  const [smsMessages, setSmsMessages] = useState<any[]>([])
  const [currentUser, setCurrentUser] = useState<any>(null)

  // Local commission calculation function
  const calculateLiveCommission = (
    amount: number,
    basePoints: number,
    bonusPoints: number,
    volumePoints: number,
    financialSettings: { agencyFeePercentage: number; closingFeeAmount: number }
  ): Commission => {
    const totalPoints = (basePoints || 0) + (bonusPoints || 0) + (volumePoints || 0)
    const totalPercentageDecimal = totalPoints / 10000 // Convert points to decimal (100 points = 1% = 0.01)
    const totalPercentageDisplay = totalPoints / 100 // For display (100 points = 1%)
    const grossCommission = (amount || 0) * totalPercentageDecimal
    const agencyFee = grossCommission * ((financialSettings.agencyFeePercentage || 0) / 100)
    const netCommission = Math.max(0, grossCommission - agencyFee - (financialSettings.closingFeeAmount || 0))
    
    return {
      totalPoints,
      totalPercentage: totalPercentageDisplay,
      grossCommission,
      agencyFee,
      closingFeeAmount: financialSettings.closingFeeAmount || 0,
      netCommission
    }
  }

  useEffect(() => {
    if (deal.id) {
      setEditedDeal(deal)
      setCustomerSearch('')
      fetchDealDetails()
      fetchDealCustomers()
      fetchDealTypes()
      // Only fetch statuses if not provided via props
      if (!statuses) {
        fetchDealStatuses()
      }
      fetchFinancialSettings()
      fetchCurrentUser()
      fetchSMSMessages()
    }
  }, [deal, statuses])

  useEffect(() => {
    if (customerSearch.trim() && customerSearch !== editedDeal.customer_name) {
      searchCustomers(customerSearch)
    } else {
      setCustomers([])
      setShowCustomerDropdown(false)
    }
  }, [customerSearch])

  // Real-time commission calculation
  useEffect(() => {
    const amount = parseFloat(editedDeal.amount) || 0
    const basePoints = parseInt(editedDeal.base_points) || 0
    const bonusPoints = parseInt(editedDeal.bonus_points) || 0
    const volumePoints = parseInt(editedDeal.volume_points) || 0

    const liveCalc = calculateLiveCommission(
      amount,
      basePoints,
      bonusPoints,
      volumePoints,
      financialSettings
    )

    setLiveCommission(liveCalc)
  }, [
    editedDeal.amount,
    editedDeal.base_points,
    editedDeal.bonus_points,
    editedDeal.volume_points,
    financialSettings
  ])

  const fetchDealDetails = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/deals/${deal.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setHistory(Array.isArray(data.history) ? data.history : [])
        setCommission(data.commission)
        setEditedDeal(prev => ({ ...prev, ...data }))
      } else {
        setHistory([])
      }
    } catch (error) {
      console.error('Error fetching deal details:', error)
      setHistory([])
    } finally {
      setLoading(false)
    }
  }

  const fetchDealTypes = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall('api/settings/deal-types', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDealTypes(Array.isArray(data) ? data : [])
      } else {
        setDealTypes([])
      }
    } catch (error) {
      console.error('Error fetching deal types:', error)
      setDealTypes([])
    }
  }

  const fetchDealStatuses = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall('api/deals/statuses', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDealStatuses(Array.isArray(data) ? data : [])
      } else {
        setDealStatuses([])
      }
    } catch (error) {
      console.error('Error fetching deal statuses:', error)
      setDealStatuses([])
    }
  }

  const fetchFinancialSettings = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall('api/settings/financial', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        const settings = data.settings || []
        
        const agencyFeeSetting = settings.find((s: any) => s.setting_key === 'agency_fee_percentage')
        const closingFeeSetting = settings.find((s: any) => s.setting_key === 'closing_fee_amount')
        
        setFinancialSettings({
          agencyFeePercentage: parseFloat(agencyFeeSetting?.setting_value || '0'),
          closingFeeAmount: parseFloat(closingFeeSetting?.setting_value || '0')
        })
      }
    } catch (error) {
      console.error('Error fetching financial settings:', error)
    }
  }

  const fetchSMSMessages = async () => {
    try {
      const token = localStorage.getItem('token')
      
      // First, get all conversations to find this customer's conversation
      const conversationsResponse = await makeApiCall(`api/sms/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!conversationsResponse.ok) {
        console.error('Failed to fetch conversations')
        setSmsMessages([])
        return
      }

      const conversationsData = await conversationsResponse.json()
      const conversations = conversationsData.conversations || []
      
      // Find conversation for this customer
      let customerConversation = null
      
      // First try to find by customer_id directly
      customerConversation = conversations.find((conv: any) => 
        conv.contact_type === 'customer' && conv.customer_id === deal.customer_id
      )
      
      // If not found, try to find by phone number matching
      if (!customerConversation) {
        const customerPhones = [deal.customer_phone, ...dealCustomers.map((dc: any) => dc.phone)].filter(Boolean)
        
        for (const phone of customerPhones) {
          if (phone) {
            // Clean phone number for comparison (last 10 digits)
            const cleanPhone = phone.replace(/[^\d]/g, '').slice(-10)
            customerConversation = conversations.find((conv: any) => {
              if (conv.contact_phone) {
                const cleanContactPhone = conv.contact_phone.replace(/[^\d]/g, '').slice(-10)
                return cleanContactPhone === cleanPhone
              }
              return false
            })
            if (customerConversation) break
          }
        }
      }

      if (!customerConversation) {
        console.log('No SMS conversation found for this customer')
        setSmsMessages([])
        return
      }

      // Now fetch the actual messages for this conversation
      const messagesResponse = await makeApiCall(
        `api/sms/conversations/${customerConversation.contact_type}/${customerConversation.contact_id}/messages?limit=200`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (messagesResponse.ok) {
        const messagesData = await messagesResponse.json()
        const messages = messagesData.messages || []
        
        // Sort messages by date (oldest first for conversation flow)
        setSmsMessages(messages.sort((a: any, b: any) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        ))
      } else {
        console.error('Failed to fetch messages for conversation')
        setSmsMessages([])
      }
      
    } catch (error) {
      console.error('Error fetching SMS messages:', error)
      setSmsMessages([])
    }
  }


  const fetchDealCustomers = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/deals/${deal.id}/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDealCustomers(data.customers || [])
      }
    } catch (error) {
      console.error('Error fetching deal customers:', error)
      setDealCustomers([])
    }
  }

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall('api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setCurrentUser(data.user)
      }
    } catch (error) {
      console.error('Error fetching current user:', error)
    }
  }

  const handleUpdateDeal = async () => {
    try {
      const response = await makeJsonApiCall(`api/deals/${deal.id}`, 'PUT', editedDeal)

      if (response.ok) {
        const data = await response.json()
        onSave(data.deal)
        onClose()
      }
    } catch (error) {
      console.error('Error updating deal:', error)
    }
  }

  const searchCustomers = async (searchTerm: string) => {
    try {
      const response = await makeApiCall(`api/deals/customers/search?q=${encodeURIComponent(searchTerm)}&limit=10`)

      if (response.ok) {
        const data = await response.json()
        setCustomers(Array.isArray(data.customers) ? data.customers : [])
        setShowCustomerDropdown(true)
      } else {
        setCustomers([])
        setShowCustomerDropdown(false)
      }
    } catch (error) {
      console.error('Error searching customers:', error)
      setCustomers([])
      setShowCustomerDropdown(false)
    }
  }

  const handleDeleteDeal = async () => {
    if (!window.confirm('Are you sure you want to delete this deal? This action cannot be undone.')) {
      return
    }

    try {
      const response = await makeApiCall(`api/deals/${deal.id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        onDelete(deal)
        onClose()
      }
    } catch (error) {
      console.error('Error deleting deal:', error)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return

    try {
      setAddingComment(true)
      const response = await makeJsonApiCall(`api/deals/${deal.id}/comments`, 'POST', {
        comment: newComment,
        comment_type: commentType
      })

      if (response.ok) {
        setNewComment('')
        setCommentType('comment') // Reset to default
        // Refresh history to show the new comment
        await fetchDealDetails()
      } else {
        console.error('Failed to add comment:', await response.text())
      }
    } catch (error) {
      console.error('Error adding comment:', error)
    } finally {
      setAddingComment(false)
    }
  }

  const handleAddCustomerToDeal = async (customer: Customer) => {
    try {
      // Check if customer is already in the deal
      if (dealCustomers.some(dc => dc.customer_id === customer.id)) {
        alert('This customer is already added to the deal')
        return
      }

      // Determine role - first customer is applicant, others are co-applicants
      const role = dealCustomers.length === 0 ? 'applicant' : 'co_applicant'
      
      // Add customer to deal
      const response = await makeJsonApiCall(`api/deals/${deal.id}/customers`, 'POST', {
        customer_id: customer.id,
        role: role
      })

      if (response.ok) {
        // Refresh deal customers
        await fetchDealCustomers()
        setCustomerSearch('')
        setShowCustomerDropdown(false)
      } else {
        const errorData = await response.json()
        alert(errorData.error || 'Failed to add customer to deal')
      }
    } catch (error) {
      console.error('Error adding customer to deal:', error)
      alert('Failed to add customer to deal')
    }
  }

  const handleChangeCustomerRole = async (dealCustomerId: number, newRole: 'applicant' | 'co_applicant') => {
    try {
      // Ensure there's always exactly one applicant
      if (newRole === 'applicant') {
        const currentApplicant = dealCustomers.find(dc => dc.role === 'applicant')
        if (currentApplicant && currentApplicant.id !== dealCustomerId) {
          if (!confirm('This will change the current applicant to co-applicant. Continue?')) {
            return
          }
        }
      }

      const response = await makeJsonApiCall(`api/deals/${deal.id}/customers/${dealCustomerId}`, 'PUT', {
        role: newRole
      })

      if (response.ok) {
        await fetchDealCustomers()
      } else {
        const errorData = await response.json()
        alert(errorData.error || 'Failed to update customer role')
      }
    } catch (error) {
      console.error('Error changing customer role:', error)
      alert('Failed to update customer role')
    }
  }

  const handleRemoveCustomer = async (dealCustomerId: number) => {
    try {
      const customerToRemove = dealCustomers.find(dc => dc.id === dealCustomerId)
      if (!customerToRemove) return

      // Prevent removing the last customer or applicant if it's the only one
      if (dealCustomers.length === 1) {
        alert('Cannot remove the last customer from the deal')
        return
      }

      if (customerToRemove.role === 'applicant' && dealCustomers.filter(dc => dc.role === 'applicant').length === 1) {
        alert('Cannot remove the applicant. Please assign another customer as applicant first.')
        return
      }

      if (!confirm(`Remove ${customerToRemove.name} from this deal?`)) {
        return
      }

      const response = await makeApiCall(`api/deals/${deal.id}/customers/${dealCustomerId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await fetchDealCustomers()
      } else {
        const errorData = await response.json()
        alert(errorData.error || 'Failed to remove customer from deal')
      }
    } catch (error) {
      console.error('Error removing customer from deal:', error)
      alert('Failed to remove customer from deal')
    }
  }

  const handleBasePointsChange = (value: string) => {
    setEditedDeal(prev => ({
      ...prev,
      base_points: value
    }))
  }

  const handleBonusPointsChange = (value: string) => {
    setEditedDeal(prev => ({
      ...prev,
      bonus_points: value
    }))
  }

  const handleVolumePointsChange = (value: string) => {
    setEditedDeal(prev => ({
      ...prev,
      volume_points: value
    }))
  }

  const handleDealTypeChange = (value: string) => {
    setEditedDeal(prev => ({
      ...prev,
      deal_type_id: value
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const token = localStorage.getItem('token')
      const updateData = {
        name: editedDeal.title,
        amount: editedDeal.amount,
        customer_id: editedDeal.customer_id,
        deal_type_id: editedDeal.deal_type_id,
        base_points: editedDeal.base_points,
        bonus_points: editedDeal.bonus_points,
        volume_points: editedDeal.volume_points,
        status_id: editedDeal.status_id,
        ...(editedDeal.expected_close_date && { expected_close_date: editedDeal.expected_close_date }),
        ...(editedDeal.closing_date && { closing_date: editedDeal.closing_date }),
        ...(editedDeal.description && { description: editedDeal.description })
      }
      const response = await makeApiCall(`api/deals/${deal.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      })

      if (response.ok) {
        const updatedDeal = await response.json()
        onSave(updatedDeal)
        await fetchDealDetails() // Refresh history and commission
      } else {
        const errorData = await response.json()
        alert(errorData.error || 'Failed to save deal')
      }
    } catch (error) {
      console.error('Error saving deal:', error)
      alert('Failed to save deal')
    } finally {
      setSaving(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatFieldChange = (fieldName: string, previousValues: string | null, newValues: string | null) => {
    if (!previousValues || !newValues) {
      return `Changed: ${fieldName}`
    }

    try {
      const oldVals = JSON.parse(previousValues)
      const newVals = JSON.parse(newValues)
      
      // Format different field types
      const formatValue = (field: string, value: any) => {
        if (field.includes('amount') || field.includes('points')) {
          if (field.includes('amount')) {
            return formatCurrency(parseFloat(value) || 0)
          }
          return `${value} points`
        }
        if (field.includes('date')) {
          return new Date(value).toLocaleDateString()
        }
        return value
      }

      const changes = []
      for (const [key, newVal] of Object.entries(newVals)) {
        const oldVal = oldVals[key]
        if (oldVal !== newVal) {
          const formattedOld = formatValue(key, oldVal)
          const formattedNew = formatValue(key, newVal)
          changes.push(`${key.replace(/_/g, ' ')} from ${formattedOld} to ${formattedNew}`)
        }
      }
      
      return changes.length > 0 ? `Changed ${changes.join(', ')}` : `Updated ${fieldName}`
    } catch {
      return `Changed: ${fieldName}`
    }
  }

  if (!deal.id) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
              <DollarIcon size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {deal.id ? 'Manage Deal' : 'Create Deal'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">{deal.title}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Scenarios Button */}
            <button
              onClick={() => window.location.href = `/dashboard/deals/${deal.id}/scenarios`}
              className="flex items-center px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors shadow-sm"
            >
              <span className="mr-2">📊</span>
              Scenarios
            </button>

            {/* PDF Export Button */}
            <div className="flex items-center space-x-2">
              <select
                value={pdfLanguage}
                onChange={(e) => setPdfLanguage(e.target.value as 'en' | 'fr')}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              >
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
              
              {currentUser && (
                <PDFDownloadLink
                  document={
                    <DealPDF
                      deal={editedDeal}
                      user={currentUser}
                      customer={{
                        id: deal.customer_id,
                        name: deal.customer_name,
                        email: deal.customer_email,
                        phone: deal.customer_phone
                      }}
                      persons={dealCustomers.map((dc: any) => ({
                        id: dc.customer_id,
                        name: dc.name,
                        email: dc.email,
                        phone: dc.phone,
                        is_primary: dc.role === 'applicant'
                      }))}
                      history={history.filter(e => e.action_type === 'comment')}
                      smsMessages={smsMessages}
                      language={pdfLanguage}
                    />
                  }
                  fileName={`deal-${deal.title || deal.name}-${new Date().toISOString().split('T')[0]}.pdf`}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg hover:from-red-600 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl text-sm font-medium"
                >
                  {({ loading }) => (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>{loading ? 'Generating...' : 'Export PDF'}</span>
                    </>
                  )}
                </PDFDownloadLink>
              )}
            </div>

            <button
              onClick={() => setShowCreateTaskModal(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <PlusIcon size={16} />
              <span className="font-medium">Create Task</span>
            </button>
            <button
              onClick={onClose}
              className="p-3 hover:bg-white/20 dark:hover:bg-gray-700/50 rounded-xl transition-colors"
            >
              <XIcon size={24} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Status Banner - Shows for all deals, with special treatment for archived */}
        <div 
          className="px-8 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
          style={{ backgroundColor: editedDeal.status_color ? `${editedDeal.status_color}20` : '#f3f4f6' }}
        >
          <div className="flex items-center space-x-3">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: editedDeal.status_color }}
            />
            {editedDeal.status_is_archived ? (
              <div className="flex items-center space-x-3">
                <span className="font-semibold text-gray-900 dark:text-white">Status:</span>
                <select
                  value={editedDeal.status_id || ''}
                  onChange={(e) => {
                    const newStatusId = e.target.value
                    const newStatus = dealStatuses.find(s => s.id === newStatusId)
                    setEditedDeal(prev => ({ 
                      ...prev, 
                      status_id: newStatusId,
                      status_name: newStatus?.name || prev.status_name,
                      status_color: newStatus?.color || prev.status_color,
                      status_is_archived: newStatus?.is_archived || false
                    }))
                  }}
                  className="px-4 py-2 border-2 border-orange-400 dark:border-orange-500 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  disabled={readOnly}
                >
                  <option value="">Select Status</option>
                  {dealStatuses && Array.isArray(dealStatuses) && dealStatuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name} {status.is_archived ? '(Archived)' : ''}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                  Select a non-archived status to reopen this deal
                </span>
              </div>
            ) : (
              <span 
                className="px-4 py-2 rounded-lg font-semibold text-white"
                style={{ backgroundColor: editedDeal.status_color }}
              >
                {editedDeal.status_name}
              </span>
            )}
          </div>
        </div>

        <div className="flex h-[calc(95vh-180px)]">
          {/* Left Panel - Deal Details */}
          <div className="flex-1 p-8 overflow-y-auto bg-gray-50/50 dark:bg-gray-900/20">
            <div className="space-y-8 max-w-4xl">
              {/* Basic Information */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                  <div className="w-2 h-2 bg-gray-800 rounded-full mr-3"></div>
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Deal Name *
                    </label>
                    <input
                      type="text"
                      value={editedDeal.title}
                      onChange={(e) => setEditedDeal(prev => ({ ...prev, title: e.target.value }))}
                      className={`w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200`}
                      readOnly={readOnly}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Deal Type
                    </label>
                    <select
                      value={editedDeal.deal_type_id || ''}
                      onChange={(e) => handleDealTypeChange(e.target.value)}
                      className={`w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200`}
                      disabled={readOnly}
                    >
                      <option value="">Select Deal Type</option>
                      {dealTypes && Array.isArray(dealTypes) && dealTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Deal Customers */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Deal Customers
                </h3>
                
                {/* Current Deal Customers */}
                <div className="space-y-3 mb-6">
                  {dealCustomers.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      No customers added to this deal yet
                    </p>
                  ) : (
                    dealCustomers
                      .sort((a, b) => a.order_position - b.order_position)
                      .map((dealCustomer, index) => (
                        <div
                          key={dealCustomer.id}
                          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                              dealCustomer.role === 'applicant' 
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' 
                                : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                            }`}>
                              {dealCustomer.role === 'applicant' ? 'Applicant' : 'Co-Applicant'}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {dealCustomer.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {dealCustomer.email || dealCustomer.phone}
                              </div>
                            </div>
                          </div>
                          
                          {!readOnly && (
                            <div className="flex items-center space-x-2">
                              {/* Role Toggle */}
                              <select
                                value={dealCustomer.role}
                                onChange={(e) => handleChangeCustomerRole(dealCustomer.id, e.target.value as 'applicant' | 'co_applicant')}
                                className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                <option value="applicant">Applicant</option>
                                <option value="co_applicant">Co-Applicant</option>
                              </select>
                              
                              {/* Remove Button */}
                              <button
                                type="button"
                                onClick={() => handleRemoveCustomer(dealCustomer.id)}
                                className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <XIcon size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))
                  )}
                </div>

                {/* Add Customer */}
                {!readOnly && (
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Add Customer
                    </label>
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Search customers by name, email, or phone..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    
                    {/* Customer Dropdown */}
                    {showCustomerDropdown && customers && Array.isArray(customers) && customers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {customers.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => handleAddCustomerToDeal(customer)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 focus:bg-gray-100 dark:focus:bg-gray-600 focus:outline-none"
                          >
                            <div className="font-medium text-gray-900 dark:text-white">
                              {customer.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {customer.email || customer.phone}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Financial Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Loan Amount *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="1000"
                    value={editedDeal.amount}
                    onChange={(e) => setEditedDeal(prev => ({ ...prev, amount: e.target.value }))}
                    className={`w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent`}
                    readOnly={readOnly}
                    required
                  />
                </div>
              </div>

              {/* Commission Points */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Commission Points
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Base Points
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={editedDeal.base_points || 0}
                      onChange={(e) => handleBasePointsChange(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent`}
                      readOnly={readOnly}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">0-999 points</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bonus Points
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={editedDeal.bonus_points || 0}
                      onChange={(e) => handleBonusPointsChange(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent`}
                      readOnly={readOnly}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">0-999 points</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Volume Points
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={editedDeal.volume_points || 0}
                      onChange={(e) => handleVolumePointsChange(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent`}
                      readOnly={readOnly}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">0-999 points</p>
                  </div>
                </div>
              </div>

              {/* Other Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Expected Close Date
                  </label>
                  <input
                    type="date"
                    value={editedDeal.expected_close_date?.split('T')[0] || ''}
                    onChange={(e) => setEditedDeal(prev => ({ ...prev, expected_close_date: e.target.value }))}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent`}
                    readOnly={readOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Actual Closing Date
                  </label>
                  <input
                    type="date"
                    value={editedDeal.closing_date?.split('T')[0] || ''}
                    onChange={(e) => setEditedDeal(prev => ({ ...prev, closing_date: e.target.value }))}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent`}
                    readOnly={readOnly}
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={editedDeal.description || ''}
                  onChange={(e) => setEditedDeal(prev => ({ ...prev, description: e.target.value }))}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${readOnly ? 'bg-gray-100 dark:bg-gray-600' : 'bg-white dark:bg-gray-700'} text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent`}
                  placeholder="Deal notes and description..."
                  readOnly={readOnly}
                />
              </div>

              {/* Commission Calculation */}
              {liveCommission && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                    <div className="w-2 h-2 bg-gray-800 rounded-full mr-3"></div>
                    Commission Calculation
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Total Points</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{liveCommission.totalPoints}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Percentage</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{liveCommission.totalPercentage.toFixed(2)}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Gross Commission</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(liveCommission.grossCommission)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Agency Fee</p>
                      <p className="font-semibold text-gray-900 dark:text-white">-{formatCurrency(liveCommission.agencyFee)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Closing Fee</p>
                      <p className="font-semibold text-gray-900 dark:text-white">-{formatCurrency(liveCommission.closingFeeAmount)}</p>
                    </div>
                    <div className="md:col-span-3">
                      <p className="text-gray-600 dark:text-gray-400">Net Commission</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">{formatCurrency(liveCommission.netCommission)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Debug Section - Admin Only */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 shadow-sm border border-yellow-200 dark:border-yellow-800">
                <h3 className="text-xl font-semibold text-yellow-800 dark:text-yellow-200 mb-6 flex items-center">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                  🐛 Debug Information (Admin Only)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  {/* Database Fields */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 border-b border-yellow-200 dark:border-yellow-700 pb-1">
                      Database Fields
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">ID:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Name:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.name || deal.title}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Status ID:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.status_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Status Name:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.status_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Customer ID:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.customer_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Customer Name:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Position:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.position}</span>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 border-b border-yellow-200 dark:border-yellow-700 pb-1">
                      Financial Data
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Amount:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.amount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Base Points:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.base_points}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Bonus Points:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.bonus_points}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Volume Points:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.volume_points}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Deal Type ID:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100">{deal.deal_type_id || 'NULL'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Financial Flag (Most Important) */}
                  <div className="md:col-span-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 dark:text-red-200 mb-3 flex items-center">
                      🏦 Financial Flag Status
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-red-700 dark:text-red-300">Current Financial Flag:</span>
                        <span className={`font-mono px-3 py-1 rounded-full text-sm font-medium ${
                          deal.financial_flag === 'pipeline' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                          deal.financial_flag === 'funded' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                          deal.financial_flag === 'open_revenue' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                          deal.financial_flag === 'paid' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                        }`}>
                          {deal.financial_flag || 'NOT SET'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Status Flags */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 border-b border-yellow-200 dark:border-yellow-700 pb-1">
                      Status Flags
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-yellow-700 dark:text-yellow-300">is_closed:</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          deal.status_is_closed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {deal.status_is_closed ? 'TRUE' : 'FALSE'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-yellow-700 dark:text-yellow-300">is_pending_compliance:</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          deal.status_is_pending_compliance ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {deal.status_is_pending_compliance ? 'TRUE' : 'FALSE'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-yellow-700 dark:text-yellow-300">is_archived:</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          deal.status_is_archived ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {deal.status_is_archived ? 'TRUE' : 'FALSE'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 border-b border-yellow-200 dark:border-yellow-700 pb-1">
                      Timestamps
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Created:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100 text-xs">{deal.created_at}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Updated:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100 text-xs">{deal.updated_at}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700 dark:text-yellow-300">Financial Flag Updated:</span>
                        <span className="font-mono text-yellow-900 dark:text-yellow-100 text-xs">{deal.financial_flag_updated_at || 'NOT SET'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Raw JSON Data (Collapsed) */}
                <details className="mt-6">
                  <summary className="cursor-pointer font-semibold text-yellow-800 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100">
                    📄 Raw Deal Object (Click to expand)
                  </summary>
                  <pre className="mt-3 p-4 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg text-xs font-mono text-yellow-900 dark:text-yellow-100 overflow-auto max-h-64">
                    {JSON.stringify(deal, null, 2)}
                  </pre>
                </details>
              </div>

              {/* Save Button - Hide in read-only mode */}
              {!readOnly && (
                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <SaveIcon size={16} className="mr-2" />
                        Save Changes
                      </div>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Comments */}
          <div className="w-96 border-l border-gray-200 dark:border-gray-700 flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Comments
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : (
                history && Array.isArray(history) && history.filter(e => e.action_type === 'comment').map((entry) => {
                  // Format comment type for display
                  const getCommentTypeDisplay = (type: string) => {
                    switch(type) {
                      case 'internal_note': return 'Internal Note'
                      case 'call': return 'Call'
                      case 'text': return 'Text/SMS'
                      case 'email': return 'Email'
                      case 'in_person': return 'In-Person'
                      case 'other': return 'Other'
                      default: return 'Comment'
                    }
                  }

                  const getCommentTypeColor = (type: string) => {
                    switch(type) {
                      case 'internal_note': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                      case 'call': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                      case 'text': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                      case 'email': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
                      case 'in_person': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
                      case 'other': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                    }
                  }

                  return (
                  <div key={entry.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCommentTypeColor(entry.comment_type || 'comment')}`}>
                          {getCommentTypeDisplay(entry.comment_type || 'comment')}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(entry.created_at)}
                      </span>
                    </div>
                    
                    <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {entry.first_name && entry.last_name 
                        ? `${entry.first_name} ${entry.last_name}`
                        : entry.created_by_username ? 
                          entry.created_by_username.split('@')[0].replace(/\./g, ' ').split(' ').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                          ).join(' ') : 
                          'Unknown User'
                      }
                    </div>
                    
                    {entry.comment && (
                      <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        {entry.comment}
                      </div>
                    )}
                  </div>
                  )
                })
              )}
            </div>

            {/* Add Comment */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type
                  </label>
                  <select
                    value={commentType}
                    onChange={(e) => setCommentType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  >
                    <option value="comment">General Comment</option>
                    <option value="internal_note">Internal Note</option>
                    <option value="call">Phone Call</option>
                    <option value="text">Text/SMS</option>
                    <option value="email">Email</option>
                    <option value="in_person">In-Person Meeting</option>
                    <option value="other">Other Communication</option>
                  </select>
                </div>
                <textarea
                  rows={3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                />
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || addingComment}
                  className="w-full px-3 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {addingComment ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Adding...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <MessageIcon size={16} className="mr-2" />
                      Add Comment
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Task Modal - DEAL FOCUSED */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Quick Task
              </h3>
              <button
                onClick={() => setShowCreateTaskModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault()
              const formData = new FormData(e.currentTarget)
              const description = formData.get('description') as string
              const selectedDate = formData.get('selected_date') as string
              const customDate = formData.get('custom_date') as string
              
              try {
                const token = localStorage.getItem('token')
                if (!token) throw new Error('Authentication required')

                // Use selected date or custom date, default to tomorrow
                let taskDueDate = selectedDate || customDate
                if (!taskDueDate) {
                  const tomorrow = new Date()
                  tomorrow.setDate(tomorrow.getDate() + 1)
                  taskDueDate = tomorrow.toISOString().split('T')[0]
                }

                const taskData = {
                  customer_id: deal.customer_id,
                  deal_id: deal.id,
                  description,
                  due_date: taskDueDate,
                  status: 'pending'
                }
                const response = await makeApiCall('api/tasks', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(taskData)
                })

                if (!response.ok) throw new Error('Failed to create task')

                setShowCreateTaskModal(false)
                alert('Task created successfully!')
              } catch (err) {
                console.error('Error creating task:', err)
                alert('Failed to create task. Please try again.')
              }
            }}>
              <div className="space-y-6">
                {/* Deal Context */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-blue-800 dark:text-blue-200">{deal.title}</div>
                      <div className="text-sm text-gray-900 dark:text-blue-300">{deal.customer_name}</div>
                    </div>
                    <div className="text-xs text-gray-700 dark:text-gray-600 bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded">
                      DEAL TASK
                    </div>
                  </div>
                </div>

                {/* Task Description - AUTO FOCUS */}
                <div>
                  <textarea
                    name="description"
                    placeholder="What needs to be done for this deal?"
                    className="w-full px-4 py-3 text-lg border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:border-gray-900 focus:outline-none transition-colors"
                    rows={2}
                    required
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.metaKey) {
                        e.preventDefault()
                        e.currentTarget.form?.requestSubmit()
                      }
                    }}
                  />
                </div>

                {/* Quick Date Selection */}
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    When is this due?
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {Array.from({ length: 7 }, (_, i) => {
                      const date = new Date()
                      date.setDate(date.getDate() + i)
                      
                      let label = ''
                      if (i === 0) label = 'Today'
                      else if (i === 1) label = 'Tomorrow'
                      else label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                      
                      return (
                        <label key={i} className="flex">
                          <input
                            type="radio"
                            name="selected_date"
                            value={date.toISOString().split('T')[0]}
                            className="sr-only peer"
                          />
                          <div className="w-full p-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-center cursor-pointer transition-all peer-checked:border-gray-900 peer-checked:bg-blue-50 dark:peer-checked:bg-blue-900/20 hover:border-gray-300">
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 peer-checked:text-blue-700 dark:peer-checked:text-blue-300">
                              {label}
                            </div>
                          </div>
                        </label>
                      )
                    })}
                  </div>

                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Or:</span>
                    <input
                      type="date"
                      name="custom_date"
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  ⌘ + Enter to create
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateTaskModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
                  >
                    Create Task
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default DealEditModal 